/**
 * AuthCallback.jsx - OAuth Callback Handler for Vite SPA
 *
 * This component handles the OAuth redirect from Supabase/Google.
 * It waits for the session to be established before redirecting to /app.
 *
 * GUARDIAN PROTECTED - Part of OAuth flow
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('Processing sign in...')
  const [error, setError] = useState(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error from OAuth provider
        const errorParam = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')

        if (errorParam) {
          console.error('[AuthCallback] OAuth error:', errorParam, errorDescription)
          setError(errorDescription || errorParam)
          setTimeout(() => navigate('/', { replace: true }), 3000)
          return
        }

        setStatus('Completing sign in...')

        // Check if we have a hash with tokens (implicit flow)
        const hash = window.location.hash
        if (hash && hash.includes('access_token')) {
          console.log('[AuthCallback] Found tokens in hash, parsing manually')

          // Parse hash fragment into object
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')

          if (accessToken) {
            console.log('[AuthCallback] Setting session from hash tokens')

            // Set the session using the tokens from the URL
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            })

            if (sessionError) {
              console.error('[AuthCallback] Error setting session:', sessionError)
              setError(sessionError.message)
              setTimeout(() => navigate('/', { replace: true }), 3000)
              return
            }

            if (data?.session) {
              console.log('[AuthCallback] Session established from hash tokens:', data.session.user?.email)
              setStatus('Sign in successful! Redirecting...')
              // Clear the hash from URL for security
              window.history.replaceState(null, '', window.location.pathname)
              navigate('/app', { replace: true })
              return
            }
          }
        }

        // Check for code (PKCE flow)
        const code = searchParams.get('code')
        if (code) {
          console.log('[AuthCallback] Found authorization code, exchanging for session')

          // Exchange code for session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)

          if (exchangeError) {
            console.error('[AuthCallback] Code exchange error:', exchangeError)
            setError(exchangeError.message)
            setTimeout(() => navigate('/', { replace: true }), 3000)
            return
          }

          if (data?.session) {
            console.log('[AuthCallback] Session established successfully')
            setStatus('Sign in successful! Redirecting...')
            navigate('/app', { replace: true })
            return
          }
        }

        // Wait a moment for detectSessionInUrl to process
        setStatus('Verifying session...')

        // Give Supabase a moment to process URL tokens
        await new Promise(resolve => setTimeout(resolve, 500))

        // Check if session was established
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('[AuthCallback] Session error:', sessionError)
          setError(sessionError.message)
          setTimeout(() => navigate('/', { replace: true }), 3000)
          return
        }

        if (session) {
          console.log('[AuthCallback] Session verified:', session.user?.email)
          setStatus('Sign in successful! Redirecting...')
          navigate('/app', { replace: true })
        } else {
          console.log('[AuthCallback] No session found, redirecting to login')
          setStatus('No session found. Redirecting...')
          setTimeout(() => navigate('/', { replace: true }), 1500)
        }
      } catch (err) {
        console.error('[AuthCallback] Unexpected error:', err)
        setError(err.message || 'An unexpected error occurred')
        setTimeout(() => navigate('/', { replace: true }), 3000)
      }
    }

    handleCallback()
  }, [navigate, searchParams])

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-400 text-xl mb-4">Sign in failed</div>
            <div className="text-gray-400">{error}</div>
            <div className="text-gray-500 mt-4 text-sm">Redirecting to login...</div>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <div className="text-white text-xl">{status}</div>
          </>
        )}
      </div>
    </div>
  )
}
